import last from "lodash/last";
import jschardet from "jschardet";
import System from "logic/src/objects/systerm/model";
import {
  SUPPORTED_IMAGE_TYPE,
  MIN_IMAGE_PX,
  // MAX_UPLOAD_FILE_SIZE,
  MAX_IMAGE_PX,
  FILE_STATUS_TEXT,
} from "../constants";
import {
  TRichModalFileStatus,
  IRichModalFile,
  ERichModalFileNormStatus,
  ERichModalFileParseStatus,
  EFileType,
} from "../types";
import { ReactComponent as WordIcon } from "../../../components/icons/documents-icon/word.svg";
import { ReactComponent as PdfIcon } from "../../../components/icons/documents-icon/pdf.svg";
import { ReactComponent as TxtIcon } from "../../../components/icons/documents-icon/txt.svg";
import { ReactComponent as PptIcon } from "../../../components/icons/documents-icon/ppt.svg";
import { ReactComponent as EpubIcon } from "../../../components/icons/documents-icon/epub.svg";
import { ReactComponent as MobiIcon } from "../../../components/icons/documents-icon/mobi.svg";
import { ReactComponent as FileIcon } from "../../../components/icons/file.svg";
import { ValidateFileListRes } from "../types";

export function isImageFileName(name?: string) {
  if (!name) return false;
  const extension = last(name.split("."));
  const result =
    extension && SUPPORTED_IMAGE_TYPE.includes(extension.toLowerCase());

  return result;
}

export function isFileNameAccept(accept: string, name?: string) {
  if (!name) return false;
  const extension = last(name.split("."));
  const acceptStr = accept.toLocaleLowerCase();
  return (
    extension &&
    (acceptStr.includes("." + extension.toLowerCase() + ",") ||
      acceptStr.endsWith("." + extension.toLowerCase()))
  );
}

export function isExcelFileName(name?: string) {
  if (!name) return false;
  const extension = last(name.split("."));
  return extension && ["xls", "xlsx"].includes(extension.toLowerCase());
}

export function isImage(file: File) {
  return file.type.startsWith("image");
}

export function isPlainText(file: File) {
  return file.type === "text/plain";
}

export function isImageValid(file: File) {
  return new Promise(resolve => {
    const img = new Image();
    img.src = URL.createObjectURL(file);

    img.onload = function () {
      console.log(img, img.width, img.height);
      if (
        img.width >= MIN_IMAGE_PX &&
        img.height >= MIN_IMAGE_PX &&
        img.width <= MAX_IMAGE_PX &&
        img.height <= MAX_IMAGE_PX
      ) {
        resolve(true);
      } else {
        resolve(false);
      }
    };

    img.onerror = function () {
      resolve(false);
    };
  });
}

export function validateImage(file: File, min: boolean) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.src = URL.createObjectURL(file);

    img.onload = function () {
      if (
        min
          ? img.width >= MIN_IMAGE_PX && img.height >= MIN_IMAGE_PX
          : img.width <= MAX_IMAGE_PX && img.height <= MAX_IMAGE_PX
      ) {
        resolve(true);
      } else {
        resolve(false);
      }
    };

    img.onerror = function () {
      reject(false);
    };
  });
}

export async function isFileListValid(
  fileList: File[],
  maxSize: number,
): Promise<ValidateFileListRes> {
  const overSizeFiles: File[] = [];

  const extensionErrorFiles: File[] = [];

  const emptyFiles: File[] = [];

  fileList.forEach(cur => {
    if (cur.size === 0) {
      emptyFiles.push(cur);
    }
  });

  fileList.forEach(cur => {
    if (cur.size > maxSize) {
      overSizeFiles.push(cur);
    }
  });

  fileList.forEach(cur => {
    if (cur.name.endsWith(".heic.jpg") || cur.name.endsWith(".heic.JPG")) {
      extensionErrorFiles.push(cur);
    }
  });

  // const tooSmallImageResult = await Promise.all(
  //   fileList.map(item => {
  //     if (isImage(item)) {
  //       return validateImage(item, true);
  //     } else {
  //       return Promise.resolve(true);
  //     }
  //   }),
  // );

  const tooLargeImageResult = await Promise.all(
    fileList.map(item => {
      if (isImage(item)) {
        return validateImage(item, false).catch(() => {
          console.log("checkout----", item);
          extensionErrorFiles.push(item);
        });
      } else {
        return Promise.resolve(true);
      }
    }),
  );

  // const tooSmallImage: File[] = tooSmallImageResult
  //   .map((item, index) => (!item ? fileList[index] : null))
  //   .filter(it => it !== null) as File[];

  const tooLargeImage: File[] = tooLargeImageResult
    .map((item, index) => (!item ? fileList[index] : null))
    .filter(it => it !== null) as File[];

  const notUTF8PlainTextFilesOrigin: Array<File | null> = await Promise.all(
    fileList.map(async item => {
      if (isPlainText(item)) {
        try {
          const res = await checkoutUTF8(item);
          if (res) {
            return null;
          } else {
            return item;
          }
        } catch (e) {
          return item;
        }
      } else {
        return null;
      }
    }),
  );

  const notUTF8PlainTextFiles = notUTF8PlainTextFilesOrigin.filter(
    it => it !== null,
  ) as File[];

  return {
    overSizeFiles,
    // tooSmallImage,
    tooLargeImage,
    extensionErrorFiles,
    notUTF8PlainTextFiles,
    emptyFiles,
  };
}

export function pureSelectFiles(
  accept: string,
  isMultiple: boolean,
  // cb: (files: FileList) => void,
): Promise<FileList> {
  return new Promise(resolve => {
    const fileInput = document.createElement("input");
    fileInput.type = "file";
    fileInput.multiple = isMultiple;
    fileInput.accept = accept;
    fileInput.style.display = "none";

    if (System.isSafari || !System.isAndroid) {
      document.body.appendChild(fileInput);
    }

    function handleFileSelect() {
      const files = fileInput.files;
      console.log("selectedfiles-1->", files);
      if (files) {
        if (System.isSafari) {
          document.body.removeChild(fileInput);
        }
        resolve(files);
      }
    }
    fileInput.addEventListener("change", handleFileSelect);

    fileInput.click();
  });
}

// export function selectFiles(
//   accept: string,
//   maxSize: number,
//   isMultiple: boolean,
//   acceptTypes?: string[],
// ): Promise<{
//   files: File[];
//   validateResult: ValidateFileListRes;
// }> {
//   return new Promise((r, j) => {
//     pureSelectFiles(accept, isMultiple, handleFileSelect);

//     function handleFileSelect(files: FileList) {
//       if (!files || files.length === 0) {
//         j(files);
//         return;
//       }
//       const fileList: File[] = [...files];
//       isFileListValid(fileList, maxSize, acceptTypes).then(result => {
//         r({ files: fileList, validateResult: result });
//       });
//     }
//   });
// }

// export function parseNovaStatus(file: IRichModalFile): TNovaFileStatus {
//   if (!file.uploadInfo) {
//     return ENovaFileNormStatus.uploading;
//   }

//   if (file.uploadInfo.status === "INVALID") {
//     return ENovaFileNormStatus.error;
//   }

//   if (!file.parseInfo) {
//     return ENovaFileNormStatus.parsing;
//   }

//   return file.parseInfo!.state;
// }

export function parseStatus(file: IRichModalFile): TRichModalFileStatus {
  if (!file.uploadInfo) {
    return ERichModalFileNormStatus.uploading;
  }

  if (
    file.uploadInfo.status === "error" ||
    file.uploadInfo.status === "timeout" ||
    file.uploadInfo.status === "failed"
  ) {
    return ERichModalFileNormStatus.error;
  }

  if (file.uploadInfo.status === "ferror") {
    return ERichModalFileNormStatus.formatError;
  }

  if (!file.parseInfo) {
    return ERichModalFileNormStatus.parsing;
  }

  if (
    file.parseInfo.status === ERichModalFileParseStatus.running &&
    !file.parseInfo.is_simple_file
  ) {
    return ERichModalFileNormStatus.longParsing;
  }

  if (
    file.parseInfo.status === ERichModalFileParseStatus.success &&
    !file.parseInfo.has_words &&
    !isImageFileName(file.file.name)
  ) {
    return ERichModalFileNormStatus.noWords;
  }

  return file.parseInfo!.status;
}

export const getStatusText = (v: TRichModalFileStatus): string => {
  return FILE_STATUS_TEXT[v];
};

export const getStatusTextFromFile = (file: IRichModalFile) => {
  const status = parseStatus(file);
  return getStatusText(status);
};

export const getFileStatusText = (
  file: IRichModalFile,
  maxSize: number,
  globalError: TRichModalFileStatus | null,
) => {
  // const extension = last(file.file.name.split("."));
  // console.log("---filename--", file.file.name, file.file.name.split("."));
  if (file.validateInfo) {
    switch (file.validateInfo.type) {
      case "extensionErrorFiles":
        return `不支持此格式的文件`;
      case "emptyFiles":
        return `文件为空，请上传有效文档`;
      case "notUTF8PlainTextFiles":
        return `不支持非utf-8格式的文本`;
      case "overSizeFiles":
        return `请上传${maxSize / 1024 / 1024}M内的文件`;
      case "tooLargeImage":
        return `图片分辨率超过限制`;
    }
  }

  if (
    globalError &&
    file.parseInfo?.status !== ERichModalFileParseStatus.success
  ) {
    return getStatusText(globalError);
  }

  const status = getStatusTextFromFile(file);
  if (status) {
    return status;
  } else {
    return `${getFileType(file.file.name)?.toUpperCase()}, ${Number(file.file.size / 1024 / 1024).toFixed(3)}MB`;
  }
};

export function checkoutUTF8(file: File): Promise<boolean> {
  return new Promise((r, j) => {
    const reader = new FileReader();

    reader.onload = function (event: ProgressEvent<FileReader>) {
      const fileContent = event.target?.result;
      if (fileContent) {
        const encodingInfo = jschardet.detect(fileContent as Buffer);
        const encoding = encodingInfo.encoding?.toLowerCase();
        r(encoding === "utf-8" || encoding === "ascii");
      } else {
        j();
      }
    };

    reader.onerror = function (error) {
      j(error);
    };

    reader.readAsBinaryString(file);
  });
}

export function getFileType(name?: string): EFileType | null {
  if (!name) return null;
  let extension = last(name.split("."));

  if (!extension) {
    return null;
  }

  extension = extension.toLowerCase();

  switch (extension) {
    case "doc":
    case "docx":
      return EFileType.word;
    case "ppt":
    case "pptx":
      return EFileType.ppt;
    case "pdf":
      return EFileType.pdf;
    case "txt":
      return EFileType.txt;
    case "epub":
      return EFileType.epub;
    case "mobi":
      return EFileType.mobi;
    case "md":
      return EFileType.md;
    default:
      return EFileType.other;
  }
}

export function getFileIcon(type: EFileType | null) {
  if (type === null) return FileIcon;

  switch (type) {
    case EFileType.epub:
      return EpubIcon;
    case EFileType.mobi:
      return MobiIcon;
    case EFileType.word:
      return WordIcon;
    case EFileType.txt:
      return TxtIcon;
    case EFileType.ppt:
      return PptIcon;
    case EFileType.pdf:
      return PdfIcon;
    default:
      return FileIcon;
  }
}

export function getFileIconFromName(name?: string) {
  const type = getFileType(name);
  return getFileIcon(type);
}
